// Persistent image cache so a remote image renders INSTANTLY on reload and is
// NEVER blank: a previously-seen image is shown on the very first frame, and a
// re-fetch keeps the old image on screen until the new bytes are fully ready.
//
// Two layers:
//   1. A SYNCHRONOUS data-URL layer (memory + localStorage) for small images
//      (avatars/thumbnails). Read in the `useState` initializer, so a cached
//      image paints on the first render — no async gap, no blank frame.
//   2. An ASYNC byte store registered by the host (web: an IndexedDB blob
//      store) for full-resolution media that's too large for localStorage.
//
// Sibling of `mediaUrlResolver` (media id → URL); this persists the actual
// bytes, keyed by the URL's stable path (the content-addressed S3 object key,
// which survives re-minted query signatures).

import { useEffect, useState } from "react";

import { resolveMediaUrl } from "./mediaUrlResolver";

// A bare media id (the server's content id, e.g. `dE3-l65_QwoK…`) is NOT a
// fetchable URL — it must be exchanged for a presigned download URL via the
// host-registered `resolveMediaUrl`. Directory/search/lookup responses carry
// `avatar_media_id` (a media id, not a URL), so `<Avatar imageUri={mediaId}>`
// in the start-chat / start-group pickers would otherwise never paint. We treat
// a string with no URL scheme and no path/dot as a media id and resolve it
// first. `http(s):`/`data:`/`blob:`/relative paths are untouched.
function looksLikeBareMediaId(uri: string): boolean {
	if (uri.includes("/") || uri.includes(":") || uri.includes(".")) return false;
	return uri.length > 0;
}

export interface ImageByteCache {
	/** Cached bytes for `key`, or undefined on a miss. */
	get(key: string): Promise<Blob | undefined>;
	/** Persist `blob` under `key` (best-effort). */
	put(key: string, blob: Blob): Promise<void>;
}

let cache: ImageByteCache | null = null;

/** Host registers (or clears, with `null`) its async image-byte store. */
export function registerImageByteCache(impl: ImageByteCache | null): void {
	cache = impl;
}

// ── Synchronous data-URL layer (instant first paint, never blank) ────────────

const LS_PREFIX = "ping.img.";
const LS_INDEX = "ping.img.__index";
// Only small images go in the synchronous layer (localStorage is ~5MB total and
// must stay snappy). A 64–96px avatar JPEG is a few KB; cap generously.
const MAX_SYNC_BLOB_BYTES = 160 * 1024;
const MAX_SYNC_ENTRIES = 150;
const memSync = new Map<string, string>();

// Insert into `memSync` with an LRU-ish cap mirroring MAX_SYNC_ENTRIES so the
// in-memory layer can't grow unbounded (only the localStorage index was capped).
// Re-setting an existing key refreshes its recency (delete + set moves it to the
// Map's tail); on overflow the oldest (head) entry is evicted.
function memSyncSet(key: string, dataUrl: string): void {
	if (memSync.has(key)) memSync.delete(key);
	memSync.set(key, dataUrl);
	while (memSync.size > MAX_SYNC_ENTRIES) {
		const oldest = memSync.keys().next().value;
		if (oldest === undefined) break;
		memSync.delete(oldest);
	}
}

function lsKeys(): string[] {
	try {
		const raw = localStorage.getItem(LS_INDEX);
		return raw ? (JSON.parse(raw) as string[]) : [];
	} catch {
		return [];
	}
}

function readSyncDataUrl(key: string): string | undefined {
	const m = memSync.get(key);
	if (m !== undefined) return m;
	try {
		const v = localStorage.getItem(LS_PREFIX + key);
		if (v !== null && v.length > 0) {
			memSyncSet(key, v);
			return v;
		}
	} catch {
		/* localStorage unavailable */
	}
	return undefined;
}

function writeSyncDataUrl(key: string, dataUrl: string): void {
	memSyncSet(key, dataUrl);
	try {
		let keys = lsKeys().filter((k) => k !== key);
		keys.push(key);
		// Write, evicting the oldest entries until it fits (quota-safe).
		for (;;) {
			try {
				localStorage.setItem(LS_PREFIX + key, dataUrl);
				break;
			} catch {
				const oldest = keys.shift();
				if (oldest === undefined) return;
				try {
					localStorage.removeItem(LS_PREFIX + oldest);
				} catch {
					/* ignore */
				}
			}
		}
		while (keys.length > MAX_SYNC_ENTRIES) {
			const oldest = keys.shift();
			if (oldest !== undefined) {
				try {
					localStorage.removeItem(LS_PREFIX + oldest);
				} catch {
					/* ignore */
				}
			}
		}
		localStorage.setItem(LS_INDEX, JSON.stringify(keys));
	} catch {
		/* best-effort */
	}
}

function persistDataUrl(key: string, blob: Blob): void {
	if (blob.size > MAX_SYNC_BLOB_BYTES || blob.size === 0) return;
	if (memSync.has(key)) return; // already persisted this session
	try {
		const reader = new FileReader();
		reader.onload = () => {
			if (typeof reader.result === "string") writeSyncDataUrl(key, reader.result);
		};
		reader.readAsDataURL(blob);
	} catch {
		/* best-effort */
	}
}

// ── Async network/dedup helpers ──────────────────────────────────────────────

const inflight = new Map<string, Promise<Blob | undefined>>();

/** Stable cache key: the URL path (content-addressed object key), ignoring the
 *  per-session signature query. Null for data:/blob:/relative URIs. */
function stableKey(uri: string): string | null {
	try {
		const u = new URL(uri);
		if (u.protocol === "data:" || u.protocol === "blob:") return null;
		return u.pathname.length > 1 ? u.pathname : uri;
	} catch {
		return null;
	}
}

async function fetchAndCache(key: string, uri: string): Promise<Blob | undefined> {
	const existing = inflight.get(key);
	if (existing) return existing;
	const p = (async () => {
		try {
			const resp = await fetch(uri);
			if (!resp.ok) return undefined;
			const blob = await resp.blob();
			await cache?.put(key, blob);
			persistDataUrl(key, blob);
			return blob;
		} catch {
			return undefined;
		} finally {
			inflight.delete(key);
		}
	})();
	inflight.set(key, p);
	return p;
}

// ── Ref-counted object-URL registry (revoke-race fix) ────────────────────────
//
// `URL.createObjectURL(blob)` mints a document-scoped `blob:` URL; it must be
// revoked or it leaks. The naive "create in the effect, revoke in its cleanup"
// pattern is UNSAFE for a virtualized list: the cleanup fires on unmount AND on
// every `uri` change, so a blob URL that React has already committed into a
// rendered `<Image src="blob:…">` gets revoked while the browser is still
// fetching it — the image then fails with `net::ERR_FILE_NOT_FOUND` (a dead
// `blob:` url). It also mints a brand-new object URL on every remount of the
// SAME image, churning memory.
//
// Since these images are content-addressed (`stableKey` = the S3 object path,
// signature-independent) and plaintext, the SAME bytes map to ONE object URL
// shared by every mounted consumer. We ref-count it: created on first acquire,
// and revoked ONLY after the last consumer releases AND a short grace elapses —
// so a virtualization remount (unmount immediately followed by re-mount of the
// same row) re-acquires the still-live url instead of racing its revoke.
interface ObjectUrlEntry {
	url: string;
	refs: number;
	revokeTimer: ReturnType<typeof setTimeout> | undefined;
}
const objectUrlByKey = new Map<string, ObjectUrlEntry>();
// Grace before a zero-ref object URL is actually revoked. Long enough to bridge
// a synchronous unmount→remount (list reorder / tab switch) without revoking a
// url the browser may still be painting.
const OBJECT_URL_REVOKE_GRACE_MS = 30_000;

/** Get (or mint) the shared object URL for `key`'s bytes and add a reference.
 *  Cancels any pending revoke so an in-flight image is never invalidated.
 *  Exported for unit tests (the ref-count/grace-revoke race is otherwise only
 *  reachable through the React hook, which the RN render harness can't mount). */
export function acquireObjectUrl(key: string, blob: Blob): string {
	let entry = objectUrlByKey.get(key);
	if (entry === undefined) {
		entry = { url: URL.createObjectURL(blob), refs: 0, revokeTimer: undefined };
		objectUrlByKey.set(key, entry);
	}
	if (entry.revokeTimer !== undefined) {
		clearTimeout(entry.revokeTimer);
		entry.revokeTimer = undefined;
	}
	entry.refs += 1;
	return entry.url;
}

/** Drop a reference to `key`'s object URL; revoke it once no consumer holds it
 *  and the grace window has passed (so a remount can re-acquire it live).
 *  Exported for unit tests — see `acquireObjectUrl`. */
export function releaseObjectUrl(key: string): void {
	const entry = objectUrlByKey.get(key);
	if (entry === undefined) return;
	entry.refs -= 1;
	if (entry.refs > 0) return;
	entry.refs = 0;
	if (entry.revokeTimer !== undefined) clearTimeout(entry.revokeTimer);
	entry.revokeTimer = setTimeout(() => {
		const current = objectUrlByKey.get(key);
		if (current !== undefined && current.refs <= 0) {
			URL.revokeObjectURL(current.url);
			objectUrlByKey.delete(key);
		}
	}, OBJECT_URL_REVOKE_GRACE_MS);
}

/**
 * Resolve an image `uri` to a source that:
 *  • paints a previously-seen image on the FIRST frame (never blank on reload),
 *  • keeps the current image on screen during a re-fetch until the new bytes
 *    are fully ready, then swaps instantly,
 *  • only falls back to the network URL the very first time an image is seen.
 */
export function useCachedImageUri(uri: string | undefined): string | undefined {
	// Synchronous initializer → a cached image is shown before any async work.
	const [resolved, setResolved] = useState<string | undefined>(() => {
		if (uri === undefined || uri.length === 0) return undefined;
		// A bare media id is content-addressed, so it is its OWN stable cache key
		// (the resolved presigned URL's signature rotates; the media id doesn't).
		const key = looksLikeBareMediaId(uri) ? uri : stableKey(uri);
		return key !== null ? readSyncDataUrl(key) : undefined;
	});

	useEffect(() => {
		if (uri === undefined || uri.length === 0) {
			setResolved(undefined);
			return;
		}
		const isMediaId = looksLikeBareMediaId(uri);
		const key = isMediaId ? uri : stableKey(uri);
		if (key === null) {
			// data:/blob:/relative URI with no content key — usable as-is.
			setResolved(uri);
			return;
		}
		// Show the synchronously-cached image NOW (covers a uri change to an
		// already-cached image). If there's no sync hit we deliberately do NOT
		// clear — whatever is already on screen stays until the new bytes load,
		// so a swap never flashes blank.
		const sync = readSyncDataUrl(key);
		if (sync !== undefined) setResolved(sync);

		let alive = true;
		// Track the registry key we hold a reference on (not the raw url), so the
		// cleanup RELEASES the shared, ref-counted object URL instead of revoking a
		// url another mounted <Image> may still be painting (the dead-`blob:` bug).
		let acquiredKey: string | undefined;
		void (async () => {
			const cached = cache !== null ? await cache.get(key).catch(() => undefined) : undefined;
			if (!alive) return;
			if (cached !== undefined) {
				acquiredKey = key;
				setResolved(acquireObjectUrl(key, cached));
				persistDataUrl(key, cached);
				return;
			}
			// Miss everywhere: this image has never been cached. Resolve a bare
			// media id to a presigned URL first (avatar pickers pass media ids,
			// not URLs); otherwise `fetchUri` is already a real URL.
			const fetchUri = isMediaId
				? await resolveMediaUrl(uri).catch(() => undefined)
				: uri;
			if (!alive || fetchUri === undefined) return;
			// Only fall back to a showable value if nothing is already on screen,
			// so a previously-displayed image is never replaced by a blank load.
			// For a media id never show the raw id (it's not a paintable URL).
			setResolved((prev) => prev ?? sync ?? (isMediaId ? undefined : uri));
			const blob = await fetchAndCache(key, fetchUri);
			if (!alive || blob === undefined) return;
			acquiredKey = key;
			setResolved(acquireObjectUrl(key, blob));
		})();
		return () => {
			alive = false;
			if (acquiredKey !== undefined) releaseObjectUrl(acquiredKey);
		};
	}, [uri]);

	return resolved;
}
